import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import CountrySelect from "../components/CountrySelect";
import VerifiedBadge from "../components/VerifiedBadge";
import { Search, MapPin, Briefcase, Wifi, ArrowUpRight, Sparkles } from "lucide-react";

export default function Jobs() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filters, setFilters] = useState({ q: "", country_code: "", contract_type: "", remote: false });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.country_code) params.country_code = filters.country_code;
      if (filters.contract_type) params.contract_type = filters.contract_type;
      if (filters.remote) params.remote = true;
      const r = await api.get("/jobs", { params });
      setJobs(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); api.get("/countries").then((r) => setCountries(r.data)); }, []);
  useEffect(() => { const tt = setTimeout(load, 250); return () => clearTimeout(tt); }, [filters]);

  const cMap = Object.fromEntries(countries.map((c) => [c.code, c]));

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="jobs-page">
      <div className="mec-overline mb-2">{t("jobs.overline")}</div>
      <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
        {t("jobs.title")}
      </h1>
      <p className="text-slate-600 mb-8">{t("jobs.subtitle")}</p>

      <div className="mec-card p-5 mb-6 grid md:grid-cols-12 gap-3">
        <div className="md:col-span-5 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder={t("jobs.placeholder_search")} value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="filter-q" />
        </div>
        <div className="md:col-span-3">
          <CountrySelect value={filters.country_code} onChange={(v) => setFilters({ ...filters, country_code: v })} placeholder={t("jobs.all_countries")} testId="filter-country" />
        </div>
        <div className="md:col-span-2">
          <Select value={filters.contract_type} onValueChange={(v) => setFilters({ ...filters, contract_type: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-contract"><SelectValue placeholder={t("jobs.contract")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("jobs.all_contracts")}</SelectItem>
              <SelectItem value="CDI">{t("contracts.CDI")}</SelectItem>
              <SelectItem value="CDD">{t("contracts.CDD")}</SelectItem>
              <SelectItem value="Freelance">{t("contracts.Freelance")}</SelectItem>
              <SelectItem value="Stage">{t("contracts.Stage")}</SelectItem>
              <SelectItem value="Alternance">{t("contracts.Alternance")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2 flex items-center gap-2 px-2">
          <Switch checked={filters.remote} onCheckedChange={(v) => setFilters({ ...filters, remote: v })} data-testid="filter-remote" />
          <Label className="text-sm cursor-pointer">{t("jobs.remote")}</Label>
        </div>
      </div>

      <div className="space-y-3">
        {loading && <div className="text-slate-500 text-sm">{t("common.loading")}</div>}
        {!loading && jobs.length === 0 && (
          <div className="mec-card p-12 text-center">
            <Sparkles className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600">{t("jobs.no_results")}</p>
          </div>
        )}
        {jobs.map((j) => (
          <Link key={j.id} to={`/jobs/${j.id}`} className="block" data-testid={`job-card-${j.id}`}>
            <div className="mec-card p-6 mec-card-hover">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <Badge variant="outline" className="font-mono text-[10px] tracking-widest uppercase border-slate-300">{t(`contracts.${j.contract_type}`, j.contract_type)}</Badge>
                    {j.remote && <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border border-emerald-200"><Wifi className="w-3 h-3 mr-1" />{t("jobs.remote")}</Badge>}
                    {j.sector && <Badge variant="outline" className="border-slate-200">{j.sector}</Badge>}
                  </div>
                  <h3 className="font-display text-xl font-semibold text-slate-900 mb-1">{j.title}</h3>
                  <div className="text-sm text-slate-500 mb-2 flex items-center gap-2">
                    <span>{j.employer_name}</span>
                    <VerifiedBadge status={j.employer_verification_status} />
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{cMap[j.country_code]?.flag} {cMap[j.country_code]?.name || j.country_code}{j.city ? `, ${j.city}` : ""}</span>
                    {(j.salary_min || j.salary_max) && (
                      <span className="inline-flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{j.salary_min}-{j.salary_max} {j.currency}</span>
                    )}
                  </div>
                </div>
                <ArrowUpRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
